// ============================================================================
// SuggestHomeworkForm — the trainer's "suggest homework" Drawer (client-detail).
// ----------------------------------------------------------------------------
// The trainer prescribes an exercise for one client: search the exercise catalog
// (GET /api/exercises?text=) and pick one OR type a free-text name, choose a
// type, sets/reps/weight or a duration, an optional suggested date, and notes.
// POSTs via useSuggestActivity → POST /api/activities?client_id= (the backend
// forces source='suggested', status defaults to 'planned'), then closes. The
// new homework appears violet on the trainer calendar and in the client's
// homework list. Mobile-first, Pulse, EN + 繁中.
// ============================================================================
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Banner, Button, DateField, TextField } from "../../components";
import { Drawer } from "./Drawer";
import { useExercises } from "../../hooks/exercises";
import { useSuggestActivity } from "../../hooks/activities";
import { useDebounced } from "../sessions/useDebounced";
import type { ActivityInput, Exercise } from "../../api";
import { exerciseName, type ExLocale } from "../exercises/lib";
import { cn } from "../../lib/cn";

/** Narrow the live i18n language to the catalog's two name locales. */
function exLocale(lang: string): ExLocale {
  return lang.toLowerCase().startsWith("zh") ? "zh" : "en";
}

function numOrUndef(s: string): number | undefined {
  const v = s.trim();
  if (v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

/** ISO date ("YYYY-MM-DD") -> a local-midnight instant the API normalizes to UTC. */
function dayToIso(day: string): string | undefined {
  const s = day.trim();
  if (!s) return undefined;
  const d = new Date(`${s}T00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

export interface SuggestHomeworkFormProps {
  clientId: number;
  open: boolean;
  onClose: () => void;
}

export function SuggestHomeworkForm({ clientId, open, onClose }: SuggestHomeworkFormProps) {
  const { t, i18n } = useTranslation("activity");
  const suggest = useSuggestActivity();

  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 250);
  const [picked, setPicked] = useState<Exercise | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [sets, setSets] = useState("");
  const [reps, setReps] = useState("");
  const [weight, setWeight] = useState("");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [needTitle, setNeedTitle] = useState(false);

  // Only query the catalog when the trainer is actively searching (no pick yet).
  const results = useExercises(
    debounced.trim() ? { text: debounced.trim() } : {},
  );
  const showResults = !picked && debounced.trim().length >= 2;

  const reset = () => {
    setSearch("");
    setPicked(null);
    setTitle("");
    setType("");
    setScheduled("");
    setSets("");
    setReps("");
    setWeight("");
    setDuration("");
    setNotes("");
    setNeedTitle(false);
  };

  const pick = (ex: Exercise) => {
    setPicked(ex);
    setSearch("");
  };

  const submit = () => {
    setNeedTitle(false);
    const freeTitle = title.trim();
    const hasName = picked != null || freeTitle.length > 0;
    if (!hasName) {
      setNeedTitle(true);
      return;
    }
    const body: ActivityInput = {};
    if (picked) {
      body.exercise_id = picked.id;
      body.title = exerciseName(picked, exLocale(i18n.language));
    } else {
      body.title = freeTitle;
    }
    if (type.trim()) body.type = type.trim();
    const sched = dayToIso(scheduled);
    if (sched) body.scheduled_at = sched;
    const setsN = numOrUndef(sets);
    if (setsN !== undefined) body.sets = setsN;
    const repsN = numOrUndef(reps);
    if (repsN !== undefined) body.reps = repsN;
    const weightN = numOrUndef(weight);
    if (weightN !== undefined) body.weight_kg = weightN;
    const durN = numOrUndef(duration);
    if (durN !== undefined) body.duration_min = durN;
    if (notes.trim()) body.notes = notes.trim();

    suggest.mutate(
      { clientId, body },
      {
        onSuccess: () => {
          reset();
          onClose();
        },
      },
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t("trainer.suggest.title")}
      subtitle={t("trainer.suggest.subtitle")}
      footer={
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onClose} disabled={suggest.isPending}>
            {t("log.clearPick") /* reuse "Cancel"-like label is below; keep close */}
          </Button>
          <div className="flex-1" />
          <Button onClick={submit} disabled={suggest.isPending}>
            {suggest.isPending ? t("trainer.suggest.saving") : t("trainer.suggest.save")}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Catalog search */}
        <div>
          <p className="mb-1.5 font-display text-sm font-bold text-body">
            {t("trainer.suggest.pickExercise")}
          </p>
          {picked ? (
            <div className="flex items-center justify-between gap-3 rounded-md bg-violet-soft/50 px-4 py-3 ring-1 ring-violet/20">
              <span className="truncate font-display text-sm font-bold text-body">
                {exerciseName(picked, exLocale(i18n.language))}
              </span>
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="shrink-0 text-sm font-bold text-coral transition-colors hover:text-coral-600 focus-visible:outline-none focus-visible:underline"
              >
                {t("log.clearPick")}
              </button>
            </div>
          ) : (
            <>
              <TextField
                label={t("trainer.suggest.search")}
                hideLabel
                placeholder={t("trainer.suggest.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {showResults && (
                <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-md ring-1 ring-line">
                  {results.isLoading ? (
                    <li className="px-3 py-2 text-sm text-body-soft">…</li>
                  ) : (results.data ?? []).length === 0 ? (
                    <li className="px-3 py-2 text-sm text-body-soft">
                      {t("log.noResults")}
                    </li>
                  ) : (
                    (results.data ?? []).slice(0, 20).map((ex) => (
                      <li key={ex.id}>
                        <button
                          type="button"
                          onClick={() => pick(ex)}
                          className={cn(
                            "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-bold text-body transition-colors hover:bg-surface-sunken focus-visible:outline-none focus-visible:bg-surface-sunken",
                          )}
                        >
                          <span className="truncate">
                            {exerciseName(ex, exLocale(i18n.language))}
                          </span>
                          {ex.category && (
                            <Badge tone="neutral" className="shrink-0">
                              {ex.category}
                            </Badge>
                          )}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </>
          )}
        </div>

        {/* Free text (when not picking from catalog) */}
        {!picked && (
          <TextField
            label={t("trainer.suggest.freeTextLabel")}
            placeholder={t("trainer.suggest.freeTextPlaceholder")}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (needTitle) setNeedTitle(false);
            }}
            hint={t("trainer.suggest.freeText")}
          />
        )}

        <TextField
          label={t("trainer.suggest.type")}
          placeholder={t("trainer.suggest.typePlaceholder")}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />

        <DateField
          label={t("trainer.suggest.scheduledAt")}
          value={scheduled}
          onChange={setScheduled}
        />

        <div className="grid grid-cols-3 gap-4">
          <TextField
            label={t("trainer.suggest.sets")}
            value={sets}
            onChange={(e) => setSets(e.target.value)}
            type="number"
            inputMode="numeric"
            min={0}
          />
          <TextField
            label={t("trainer.suggest.reps")}
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            type="number"
            inputMode="numeric"
            min={0}
          />
          <TextField
            label={t("trainer.suggest.weight")}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            type="number"
            inputMode="decimal"
            step="0.5"
            min={0}
            suffix={t("units.kg")}
          />
        </div>

        <TextField
          label={t("trainer.suggest.duration")}
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
          suffix={t("units.min")}
        />

        <div className="w-full">
          <label
            htmlFor="vf-suggest-notes"
            className="mb-1.5 block font-display text-sm font-bold text-body"
          >
            {t("trainer.suggest.notes")}
          </label>
          <textarea
            id="vf-suggest-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder={t("trainer.suggest.notesPlaceholder")}
            className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
          />
        </div>

        {needTitle && <Banner tone="warning" title={t("trainer.suggest.needTitle")} />}
        {suggest.isError && (
          <Banner tone="danger" title={t("trainer.suggest.saveError")} />
        )}
      </div>
    </Drawer>
  );
}
